<template>
  <div class="toolkit-tab">
    <v-card class="mb-6">
      <v-card-title class="primary--text">
        <v-icon left color="primary">mdi-toolbox</v-icon>
        Toolkit Triple A
      </v-card-title>
      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="4">
            <v-sheet rounded class="metric-sheet pa-4" :style="kpiStyleByCount(toolkitItems.length)">
              <div class="caption grey--text">Behaviours Targeted</div>
              <div class="text-h5 font-weight-bold">{{ toolkitItems.length }}</div>
            </v-sheet>
          </v-col>
          <v-col cols="12" md="4">
            <v-sheet rounded class="metric-sheet pa-4" :style="kpiStyleByCount(totalQuestions)">
              <div class="caption grey--text">Total Questions</div>
              <div class="text-h5 font-weight-bold">{{ totalQuestions }}</div>
            </v-sheet>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <div v-if="toolkitItems.length > 0">
      <v-row dense align="stretch">
        <v-col
          v-for="item in toolkitItems"
          :key="item.id"
          cols="12"
          md="6"
          class="d-flex"
        >
          <v-card class="mb-4 toolkit-behaviour-card toolkit-grid-card" elevation="2">
            <v-card-title class="text-subtitle-1">
              <div class="toolkit-card-header">
                <div class="d-flex align-center">
                  <v-icon left color="primary">mdi-eye-check</v-icon>
                  {{ item.behaviourName }}
                </div>
                <div class="mt-2 d-flex align-center flex-wrap">
                  <v-chip
                    v-if="item.selectionType"
                    small
                    outlined
                    :color="selectionChipColor(item.selectionType)"
                    class="mr-2 mb-1"
                  >
                    {{ item.selectionType }}
                  </v-chip>
                </div>
              </div>
            </v-card-title>
            <v-card-text>
              <v-list dense>
                <v-list-item
                  v-for="(question, qIndex) in item.questions"
                  :key="qIndex"
                  class="question-item"
                >
                  <v-list-item-avatar class="mr-3">
                    <v-avatar color="primary" size="28">
                      <span class="white--text caption">{{ qIndex + 1 }}</span>
                    </v-avatar>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title class="body-2 text-wrap question-text">
                      {{ question }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <v-card v-else class="text-center pa-8">
      <v-icon size="64" color="grey lighten-1">mdi-toolbox-outline</v-icon>
      <h3 class="mt-4 grey--text">No toolkit data available</h3>
      <p class="body-2 grey--text mt-2">
        The interview toolkit has not been generated yet.
      </p>
    </v-card>

    <v-card class="mt-6 legend-card" outlined>
      <v-card-title class="text-subtitle-2">
        <v-icon small left>mdi-code-json</v-icon>
        Full toolkit payload
      </v-card-title>
      <v-card-text>
        <div class="raw-json-container">
          <JsonTreeViewer
            v-if="toolkitResult && Object.keys(toolkitResult).length"
            :data="toolkitResult"
          />
          <p v-else class="grey--text mb-0">No toolkit payload available.</p>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import JsonTreeViewer from './JsonTreeViewer.vue';

export default {
  name: 'ToolkitTripleATab',
  components: { JsonTreeViewer },

  props: {
    beliefsAnalysis: {
      type: Object,
      default: () => null,
    },
  },

  computed: {
    fullInterviewResult() {
      return this.beliefsAnalysis?.fullInterviewResult || null;
    },

    toolkitResult() {
      return this.fullInterviewResult?.results?.triple_a_toolkit || null;
    },

    toolkitQuestions() {
      if (this.toolkitResult?.questions && typeof this.toolkitResult.questions === 'object') {
        return this.toolkitResult.questions;
      }
      if (
        this.toolkitResult?.questions_by_behaviour &&
        typeof this.toolkitResult.questions_by_behaviour === 'object'
      ) {
        return this.toolkitResult.questions_by_behaviour;
      }
      return {};
    },

    categorization() {
      return this.toolkitResult?.categorization || {};
    },

    nonDetectedItems() {
      return Array.isArray(this.categorization?.non_detected) ? this.categorization.non_detected : [];
    },

    lowScoreItems() {
      return Array.isArray(this.categorization?.low_score) ? this.categorization.low_score : [];
    },

    toolkitItems() {
      const items = [];
      
      Object.keys(this.toolkitQuestions).forEach((behaviourId) => {
        const toolkitData = this.toolkitQuestions[behaviourId];
        const questions = Array.isArray(toolkitData?.questions)
          ? toolkitData.questions
          : Array.isArray(toolkitData?.proposed_stages)
            ? toolkitData.proposed_stages
            : [];
        if (toolkitData && questions.length > 0) {
          items.push({
            id: behaviourId,
            behaviourName:
              toolkitData.behaviour_name ||
              toolkitData.cv_name ||
              this.visibleBehaviourNameMap[String(behaviourId)] ||
              `Behaviour ${behaviourId}`,
            category: toolkitData.category || null,
            questions,
            questionsCount: questions.length || 0,
            negativeDetections: toolkitData.negative_detections_number || 0,
            score: toolkitData.score ?? null,
            selectionType: this.selectionTypeByBehaviourId[String(behaviourId)] || null,
          });
        }
      });

      return items;
    },
    totalQuestions() {
      return this.toolkitItems.reduce((acc, item) => acc + (item.questionsCount || 0), 0);
    },
    selectionTypeByBehaviourId() {
      const map = {};
      this.nonDetectedItems.forEach((item) => {
        const id = String(item?.visible_behaviour_id || item?.cv_id || '');
        if (id) map[id] = 'non_detected';
      });
      this.lowScoreItems.forEach((item) => {
        const id = String(item?.visible_behaviour_id || item?.cv_id || '');
        if (id) map[id] = 'low_score';
      });
      return map;
    },
    visibleBehaviourNameMap() {
      const map = {};
      Object.entries(this.toolkitQuestions || {}).forEach(([id, data]) => {
        const name = data?.behaviour_name || data?.cv_name || null;
        if (name) map[String(id)] = name;
      });
      const precomp = this.fullInterviewResult?.results?.pre_competencial?.visible_behaviour || [];
      if (Array.isArray(precomp)) {
        precomp.forEach((item) => {
          const id = String(item?.visible_behaviour_id || item?.competency_id || item?.id || '');
          const name = item?.name || item?.competency_name || null;
          if (id && name && !map[id]) map[id] = name;
        });
      }
      return map;
    },
  },
  methods: {
    kpiToneColor(value) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) return '#b0bec5';
      const v = Number(value);
      if (v >= 80) return '#66bb6a';
      if (v >= 65) return '#42a5f5';
      if (v >= 45) return '#ffca28';
      return '#ef5350';
    },
    kpiStyleByPercent(percent) {
      const color = this.kpiToneColor(percent);
      return {
        backgroundColor: `${color}22`,
        border: `1px solid ${color}66`,
      };
    },
    kpiStyleByCount(count) {
      if (count === null || count === undefined || Number.isNaN(Number(count))) {
        return this.kpiStyleByPercent(null);
      }
      const c = Number(count);
      let pseudoPercent = 0;
      if (c >= 8) pseudoPercent = 85;
      else if (c >= 4) pseudoPercent = 68;
      else if (c >= 1) pseudoPercent = 50;
      else pseudoPercent = 20;
      return this.kpiStyleByPercent(pseudoPercent);
    },
    resolveBehaviourName(item) {
      const id = String(item?.visible_behaviour_id || item?.cv_id || '');
      return item?.cv_name || this.visibleBehaviourNameMap[id] || `Behaviour ${id || 'N/A'}`;
    },
    selectionChipColor(selectionType) {
      if (selectionType === 'non_detected') return 'warning';
      if (selectionType === 'low_score') return 'info';
      return 'grey';
    },
  },
};
</script>

<style scoped>
.toolkit-tab {
  padding: 16px 0;
}

.metric-sheet {
  background: #f7f9fc;
  border: 1px solid #e5eaf2;
}

.toolkit-behaviour-card {
  border-left: 4px solid #1976d2;
  transition: transform 0.2s, box-shadow 0.2s;
}

.toolkit-grid-card {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.toolkit-grid-card :deep(.v-card__text) {
  flex: 1;
}

.toolkit-behaviour-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.question-item {
  border-radius: 8px;
  margin-bottom: 8px;
  background: rgba(0, 0, 0, 0.02);
}

.question-text {
  white-space: normal !important;
  line-height: 1.5;
}

.legend-card {
  background: #fafafa;
}

.raw-json-container {
  background: #1e1e1e;
  color: #d4d4d4;
  padding: 16px;
  border-radius: 8px;
  overflow: auto;
  max-height: 70vh;
}
</style>

